import React, { useEffect, useState, useContext, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Container, Paper, Grid, Tabs, Tab, useTheme } from '@mui/material';
import { GeneralContext } from '../../../../shared/contexts/GeneralContext.context';
import TabPanel from './TabPanel';
import TabVideo from './TabPanel/TabVideo';
import TabPhone from './TabPanel/TabPhone';
import TabDetails from './TabPanel/TabDetails';
import { apiService } from '../../../../services';
import { offlineStorage } from '../../../../services/offlineStorage';
import * as translations from './intl';
import { useTranslation, useQuery } from '../../../../shared/hooks';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../../../shared/constants/globalConstants';

const Title = styled('p')(({ theme }) => ({
  fontFamily: 'Publico Headline !important',
  fontSize: '48px !important',
  textAlign: 'center',
  letterSpacing: '1px',
  fontWeight: 600,
  lineHeight: '60px !important',
  color: theme.palette.text.main,
  paddingBottom: '42px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '38px !important',
  },
}));

const StyledContainer = styled(Container)({
  marginTop: '60px',
  marginBottom: '60px',
});

const StyledTab = styled(Tab)(({ theme }) => ({
  fontFamily: "'Source Sans Pro'",
  color: theme.palette.secondary.main,
  fontWeight: 600,
  textTransform: 'uppercase',
  opacity: 0.7,
  '&.Mui-selected': {
    color: theme.palette.secondary.main,
    fontWeight: 600,
    opacity: 1,
  },
}));

function Appointments() {
  const theme = useTheme();
  const query = useQuery();
  const { amosToken, isLogged, setIsLoading, userData, adultClientToken } =
    useContext(GeneralContext);

  const { locale: intl } = useTranslation({
    translationsFile: translations,
  });
  const { APPOINTMENTS, VIDEO_CALLS, PHONE_CALLS, FULL_WIDTH_LABEL } = intl;

  const type = query.get('type');
  const initialTab = type === 'phone' ? 1 : 0;

  const [value, setValue] = useState(initialTab);
  const [typeAppointment, setTypeAppointment] = useState('');
  const [userType, setUserType] = useState('');
  const [phoneCallId, setPhoneCallId] = useState();
  const [gotTime, setGotTime] = useState();
  const [appointmentId, setAppointmentId] = useState();
  const [listRequestsVideo, setListRequestsVideo] = useState([]);
  const [listRequestsPhone, setListRequestsPhone] = useState([]);

  const removeDuplicates = useCallback((array, key = 'id') => {
    const seen = new Set();
    return array.filter((item) => {
      if (seen.has(item[key])) return false;
      seen.add(item[key]);
      return true;
    });
  }, []);

  const getTime = useCallback(async (videoItem) => {
    try {
      const timeZone = videoItem.timeZone;
      const resp = await apiService.amos.getTime(timeZone);
      return {
        ...videoItem,
        gotTime: resp.data.time,
      };
    } catch (error) {
      console.error('Error fetching time:', error);
      return videoItem;
    }
  }, []);

  const getRequestsVideo = useCallback(async () => {
    setIsLoading(true);
    try {
      const userEmail = userData?.email;
      let videocalls = [];

      // Try offline storage first if no token
      if (!amosToken && userEmail) {
        const cachedData = await offlineStorage.getVideoRequests(userEmail);
        if (cachedData?.videocalls) {
          videocalls = cachedData.videocalls;
        }
      } else if (amosToken) {
        const clientResponse = await apiService.amos.getRequestsVideo(
          amosToken,
          userEmail
        );
        const clientVideocalls = clientResponse?.data?.videocalls || [];

        if (adultClientToken) {
          const adultResponse = await apiService.amos.getRequestsVideo(
            adultClientToken,
            userEmail
          );
          const adultVideocalls = adultResponse?.data?.videocalls || [];
          videocalls = removeDuplicates([
            ...clientVideocalls,
            ...adultVideocalls,
          ]);
        } else {
          videocalls = clientVideocalls;
        }

        // Store in IndexedDB
        if (userEmail && videocalls.length > 0) {
          await offlineStorage.storeVideoRequests(userEmail, { videocalls });
        }
      }

      // Fetch server time for active appointments
      if (videocalls.length > 0) {
        const promises = videocalls.map((item) => {
          const status = String(item.status).toLowerCase();
          if (status === 'pending' || status === 'in-progress') {
            return getTime(item);
          }
          return item;
        });
        videocalls = await Promise.all(promises);
      }

      setListRequestsVideo(videocalls);
    } catch (err) {
      console.error('Error fetching video requests:', err);
      setListRequestsVideo([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    amosToken,
    adultClientToken,
    userData,
    setIsLoading,
    removeDuplicates,
    getTime,
  ]);

  const getRequestsPhone = useCallback(async () => {
    setIsLoading(true);
    try {
      const userEmail = userData?.email;
      let consultations = [];

      // Try offline storage first if no token
      if (!amosToken && userEmail) {
        const cachedData = await offlineStorage.getPhoneRequests(userEmail);
        if (cachedData?.consultations) {
          consultations = cachedData.consultations;
        }
      } else if (amosToken) {
        const clientResponse = await apiService.amos.getRequestsPhone(
          amosToken,
          userEmail
        );
        const clientConsultations = clientResponse?.data?.consultations || [];

        if (adultClientToken) {
          const adultResponse = await apiService.amos.getRequestsPhone(
            adultClientToken,
            userEmail
          );
          const adultConsultations = adultResponse?.data?.consultations || [];
          consultations = removeDuplicates([
            ...clientConsultations,
            ...adultConsultations,
          ]);
        } else {
          consultations = clientConsultations;
        }

        // Store in IndexedDB
        if (userEmail && consultations.length > 0) {
          await offlineStorage.storePhoneRequests(userEmail, {
            consultations,
          });
        }
      }

      setListRequestsPhone(consultations);
    } catch (err) {
      console.error('Error fetching phone requests:', err);
      setListRequestsPhone([]);
    } finally {
      setIsLoading(false);
    }
  }, [amosToken, adultClientToken, userData, setIsLoading, removeDuplicates]);

  const handleChange = useCallback((event, newValue) => {
    setValue(newValue);

    const videoCallInput = document.querySelector('#video_call');
    let type = '';

    if (videoCallInput && videoCallInput.value === VIDEO_CALLS) {
      type = 'video';
    } else {
      type = 'phone';
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      subsidiary_name: 'axa_partners',
      business_unit: 'customer_and_solutions',
      business_line: 'travel_solutions',
      digital_business_process: 'assistance_request',
      global_digital_asset: 'doctor_please',
      partner_id: getPartnerId(),
      partner_name: getPartnerName(),
      country: getCountryCode(),
      language: getLanguage(),
      instance_environment: getInstanceEnv(),
      user_logged: 'true',
      page_name: 'appointments list',
      appointment_type: `${type} call`,
      funnel_step: '3',
      step_name: 'appointments list',
      event: 'pageview',
    });
  }, []);

  useEffect(() => {
    if (isLogged) {
      getRequestsPhone();
      getRequestsVideo();
    } else {
      setListRequestsVideo([]);
      setListRequestsPhone([]);
    }
  }, [amosToken, adultClientToken, isLogged]);

  return (
    <div id="appointments">
      <StyledContainer>
        <Grid size={{ md: 12 }}>
          <Title>{APPOINTMENTS}</Title>
        </Grid>
        <Paper elevation={3}>
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
            aria-label={FULL_WIDTH_LABEL}
          >
            <StyledTab label={VIDEO_CALLS} />
            <StyledTab label={PHONE_CALLS} />
          </Tabs>

          <TabPanel value={value} index={0} dir={theme.direction}>
            <TabVideo
              videoList={listRequestsVideo}
              setValue={setValue}
              setAppointmentId={setAppointmentId}
              setTypeAppointment={setTypeAppointment}
              setGotTime={setGotTime}
              setUserType={setUserType}
            />
          </TabPanel>

          <TabPanel value={value} index={1} dir={theme.direction}>
            <TabPhone
              phoneList={listRequestsPhone}
              setValue={setValue}
              setPhoneCallId={setPhoneCallId}
              setTypeAppointment={setTypeAppointment}
              setUserType={setUserType}
            />
          </TabPanel>

          <TabPanel value={value} index={2} dir={theme.direction}>
            <TabDetails
              setValue={setValue}
              phoneCallId={phoneCallId}
              appointmentId={appointmentId}
              typeAppointment={typeAppointment}
              gotTime={gotTime}
              userType={userType}
            />
          </TabPanel>
        </Paper>
      </StyledContainer>
    </div>
  );
}

export default Appointments;
